import React from 'react'

import cx from 'classnames'
import { GatsbyImage } from 'gatsby-plugin-image'
import htmr from 'htmr'
import styled from 'styled-components'

import { above, below, colors, space } from '../../style/design-system'

type HeroInternalProps = {
  alt?: string
  backgroundImage?: any
  backgroundImageMobile?: any
  borderColor?: string
  children?: React.ReactNode
  className?: string
  contentLg: string
  contentSm: string
  imgStyle?: any
  invertImg?: boolean
}

const HeroInternal = ({
  alt,
  backgroundImage,
  backgroundImageMobile,
  borderColor,
  className,
  contentLg,
  contentSm,
  imgStyle,
  invertImg,
  children,
}: HeroInternalProps) => (
  <div className={className}>
    {backgroundImageMobile && (
      <GatsbyImage
        image={backgroundImageMobile.image}
        objectPosition={backgroundImageMobile.objectPosition}
        className="d-lg-none w-100"
        imgStyle={imgStyle}
        style={{ height: '300px' }}
        loading="eager"
        alt={alt}
      />
    )}
    {backgroundImage && (
      <GatsbyImage
        image={backgroundImage.image}
        objectPosition={backgroundImage.objectPosition}
        className={cx('skew-img', {
          'd-none d-lg-block': backgroundImageMobile,
        })}
        loading="eager"
        imgStyle={{ zIndex: '-1', ...imgStyle }}
        style={{
          transform: invertImg ? 'scaleX(-1)' : 'none',
        }}
        alt={alt}
      />
    )}

    <div className="skew d-none d-lg-inline-block" style={{ borderColor }}>
      <div className="skew-blue bg--blue-chev north" />
    </div>

    <div className="container">
      <div className="content position-relative">
        {contentLg && <h1 className="h1 mt-lg-0">{contentLg && <span>{htmr(contentLg)}</span>}</h1>}

        {contentSm && <p className="lead white mt-5">{htmr(contentSm)}</p>}
        {children}
      </div>
    </div>
  </div>
)

HeroInternal.defaultProps = {
  alt: '',
  backgroundImage: undefined,
  backgroundImageMobile: undefined,
  borderColor: colors.dev.green.e(0.75),
  children: undefined,
  className: '',
  imgStyle: undefined,
  invertImg: false,
}

export default styled(HeroInternal)`
  align-items: center;
  background-color: ${colors.dev.gray.h()};
  font-size: 0;
  overflow: hidden;
  position: relative;

  ${above.lg`
    display: flex;
    font-size: normal;
    min-height: 431px;
    padding: 3rem 0;
  `}

  // IE specific styles
  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    min-height: auto;
  }

  @media (min-width: 992px) and (max-width: 1199px) {
    h1 {
      font-size: 2.25rem;
    }
  }

  .container {
    ${below.xl`
      max-width: 100%;
    `}
  }

  .skew {
    border-right: 50px solid;
    display: none;
    overflow: hidden;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    transform: skewX(-45deg) translateX(-50%);
    width: 100%;

    ${above.xl`
      transform: skewX(-45deg) translateX(-50%);
    `}

    .skew-blue {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      transform: translateX(35%) skewX(45deg) rotate(180deg);
      width: 110%;
    }
  }

  .skew-img {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    display: inline-block;
    height: auto !important;
    min-height: 300px !important;
    position: relative;
    width: 100%;
    z-index: 0;

    ${above.lg`
      background-position: right center;
      position: absolute !important;
        top: 0; right: 0; bottom: 0;
      width: 82%;
    `}
  }

  .content {
    display: none;

    ${above.lg`
      display: block;
      max-width: 40%;
      padding: ${space.f} 0;
    `}

    .h1 {
      color: ${colors.dev.blue.d()};
      margin-bottom: 0;

      ${above.lg`
        color: #fff;
        margin-bottom: initial;
      `}
    }
    .h2 {
      color: ${colors.dev.blue.d()};
      margin-top: ${space.d};
      ${above.lg`
        color: #fff;
      `}
    }

    .lead {
      font-weight: normal;
      max-width: 30rem;
    }
  }
`
